// impactUtils.js

/**
 * Generates an array of start dates for a given time period.
 * @param {string} startDateString The start date in 'YYYY-MM-DD' format.
 * @param {number} totalMonths The total number of months in the data series.
 * @param {number} durationMonths The duration of each period (e.g., 1 for monthly, 3 for quarterly).
 * @returns {Array<Date>} An array of JavaScript Date objects.
 */
const generateDateSeries = (startDateString, totalMonths, durationMonths) => {
    const dates = [];
    let currentDate = new Date(startDateString);

    for (let i = 0; i < totalMonths; i += durationMonths) {
        dates.push(new Date(currentDate));
        currentDate.setMonth(currentDate.getMonth() + durationMonths);
    }
    return dates;
};

/**
 * Filters and aggregates impact data using native Date objects for performance.
 * @param {Array<Object>} impactResult The array of impact objects to process.
 * @param {Object} attributeFilters An object containing a 'period' filter with fromDate and toDate.
 * @returns {Object} An object containing the filtered impacts and the calculated sum results.
 */
export const processImpactData = (impactResult, attributeFilters) => {
    const filterFromDate = new Date(attributeFilters?.period?.fromDate);
    const filterToDate = new Date(attributeFilters?.period?.toDate);

    const positiveImpacts = ['Recurring Benefit (ETB)', 'One Time Benefit'];

    // Find the longest array length to generate the date series
    const monthlyLength = impactResult.find(i => i.monthlyPlan)?.monthlyPlan.length || 0;
    const quarterlyLength = impactResult.find(i => i.quarterlyPlan)?.quarterlyPlan.length || 0;
    const annuallyLength = impactResult.find(i => i.annuallyPlan)?.annuallyPlan.length || 0;

    // Pre-generate the date series for each period type
    const monthlyDates = generateDateSeries('2023-07-01', monthlyLength, 1);
    const quarterlyDates = generateDateSeries('2023-07-01', quarterlyLength * 3, 3);
    const annuallyDates = generateDateSeries('2023-07-01', annuallyLength * 12, 12);

    const sumResults = {
        monthlyPlan: Array(monthlyLength).fill(null),
        monthlyActual: Array(monthlyLength).fill(null),
        quarterlyPlan: Array(quarterlyLength).fill(null),
        quarterlyActual: Array(quarterlyLength).fill(null),
        annuallyPlan: Array(annuallyLength).fill(null),
        annuallyActual: Array(annuallyLength).fill(null),
    };

    const filteredImpact = impactResult.reduce((accumulator, impact) => {
        const filteredImpact = { ...impact };
        let hasDataInPeriod = false;
        const multiplier = positiveImpacts.includes(impact.frequencyAndDirection) ? 1 : -1;

        const processPeriod = (type, dates) => {
            const planKey = `${type}Plan`;
            const actualKey = `${type}Actual`;

            if (filteredImpact[planKey]) {
                filteredImpact[planKey] = filteredImpact[planKey].map((value, index) => {
                    if (value !== null) {
                        const date = dates[index];
                        if (date >= filterFromDate && date <= filterToDate) {
                            hasDataInPeriod = true;
                            sumResults[planKey][index] += value * multiplier;
                            return value;
                        }
                    }
                    return null;
                });
            }

            if (filteredImpact[actualKey]) {
                filteredImpact[actualKey] = filteredImpact[actualKey].map((value, index) => {
                    if (value !== null) {
                        const date = dates[index];
                        if (date >= filterFromDate && date <= filterToDate) {
                            hasDataInPeriod = true;
                            sumResults[actualKey][index] += value * multiplier;
                            return value;
                        }
                    }
                    return null;
                });
            }
        };

        processPeriod('monthly', monthlyDates);
        processPeriod('quarterly', quarterlyDates);
        processPeriod('annually', annuallyDates);

        // The filter logic is slightly different here, only pushing if the impact has filtered data
        if (hasDataInPeriod) {
            accumulator.push(filteredImpact);
        }

        return accumulator;
    }, []);

    return {
        filteredImpact,
        sumResults,
    };
};

export const processedKPIData = (kpiResult, attributeFilters) => {
    const filterFromDate = new Date(attributeFilters?.period?.fromDate);
    const filterToDate = new Date(attributeFilters?.period?.toDate);
    const monthlyLength = kpiResult.find(i => i.monthlyPlan)?.monthlyPlan.length || 0;
    const quarterlyLength = kpiResult.find(i => i.quarterlyPlan)?.quarterlyPlan.length || 0;
    const annuallyLength = kpiResult.find(i => i.annuallyPlan)?.annuallyPlan.length || 0;

    // Pre-generate the date series for each period type
    const monthlyDates = generateDateSeries('2023-07-01', monthlyLength, 1);
    const quarterlyDates = generateDateSeries('2023-07-01', quarterlyLength * 3, 3);
    const annuallyDates = generateDateSeries('2023-07-01', annuallyLength * 12, 12);

    const sumResults = {
        monthlyPlan: Array(monthlyLength).fill(null),
        monthlyActual: Array(monthlyLength).fill(null),
        quarterlyPlan: Array(quarterlyLength).fill(null),
        quarterlyActual: Array(quarterlyLength).fill(null),
        annuallyPlan: Array(annuallyLength).fill(null),
        annuallyActual: Array(annuallyLength).fill(null),
    };
    const filteredKPI = kpiResult.reduce((accumulator, kpi) => {
        const filteredKPI = { ...kpi };
        let hasDataInPeriod = false;
        const multiplier = 1;
        const processPeriod = (type, dates) => {
            const planKey = `${type}Plan`;
            const actualKey = `${type}Actual`;

            if (filteredKPI[planKey]) {
                filteredKPI[planKey] = filteredKPI[planKey].map((value, index) => {
                    if (value !== null) {
                        const date = dates[index];
                        if (date >= filterFromDate && date <= filterToDate) {
                            hasDataInPeriod = true;
                            sumResults[planKey][index] += value * multiplier;
                            return value;
                        }
                    }
                    return null;
                });
            }

            if (filteredKPI[actualKey]) {
                filteredKPI[actualKey] = filteredKPI[actualKey].map((value, index) => {
                    if (value !== null) {
                        const date = dates[index];
                        if (date >= filterFromDate && date <= filterToDate) {
                            hasDataInPeriod = true;
                            sumResults[actualKey][index] += value * multiplier;
                            return value;
                        }
                    }
                    return null;
                });
            }
        };

        processPeriod('monthly', monthlyDates);
        processPeriod('quarterly', quarterlyDates);
        processPeriod('annually', annuallyDates);
        // The filter logic is slightly different here, only pushing if the impact has filtered data
        if (hasDataInPeriod) {
            accumulator.push(filteredKPI);
        }

        return accumulator;
    }, []);
    return {
        filteredKPI,
        kpiResult,
    };
};